import { Link } from "react-router-dom";
import useRealtime from "../../lib/useRealtime";
import { useAuth } from "../../contexts/AuthContext";
import ScLeadBanner from "../../components/sc/ScLeadBanner";
import {
  Sparkles, Wallet, Trophy, Award, Flame, Crown, Sprout, Leaf, TreePine,
  Coins, ChevronRight, BadgeCheck, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Clock, FileWarning, PauseCircle, Users, MapPin, FileText, Wrench, CheckCircle2,
} from "lucide-react";

const ICON_MAP = { sprout: Sprout, leaf: Leaf, tree: TreePine, crown: Crown, coin: Coins, coins: Wallet, trophy: Trophy };

/** Bold, high-contrast "Today" counter boxes — the two numbers SCs check most often. */
function TodayStrip({ todayLeads, todayTickets }) {
  return (
    <div data-testid="sc-today-strip">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">Today</div>
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/sc/leads"
          data-testid="sc-today-leads"
          className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-sky-500 to-sky-700 shadow-[0_6px_20px_rgba(2,132,199,0.35)] active:scale-[0.98] transition-all"
        >
          <FileText className="w-8 h-8 text-sky-300/40 absolute -right-1 -bottom-1" />
          <div className="text-[10px] font-bold uppercase tracking-widest text-sky-100">New Requests</div>
          <div className="font-mono text-4xl font-black text-white leading-none mt-1.5">{todayLeads}</div>
          <div className="text-[10px] text-sky-100/90 mt-1">created today</div>
        </Link>
        <Link
          to="/sc/service-requests"
          data-testid="sc-today-tickets"
          className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-amber-500 to-orange-600 shadow-[0_6px_20px_rgba(217,119,6,0.35)] active:scale-[0.98] transition-all"
        >
          <Wrench className="w-8 h-8 text-amber-200/40 absolute -right-1 -bottom-1" />
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-50">Service Tickets</div>
          <div className="font-mono text-4xl font-black text-white leading-none mt-1.5">{todayTickets}</div>
          <div className="text-[10px] text-amber-50/90 mt-1">created today</div>
        </Link>
      </div>
    </div>
  );
}

/** Bold, color-blocked status breakdown for New Requests (leads) and Service
 * Requests (tickets) — big numbers, vivid fills, impossible to miss. */
function StatusInfographic({ leadStats, ticketStats }) {
  const leadTotal = Object.values(leadStats || {}).reduce((a, b) => a + b, 0) || 1;
  const leadSegs = [
    { k: "open", label: "Open", fill: "bg-sky-500", text: "text-sky-700", chip: "bg-sky-50", v: leadStats?.open || 0 },
    { k: "accepted", label: "Accepted", fill: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-50", v: leadStats?.accepted || 0 },
    { k: "converted", label: "Converted", fill: "bg-[#1B5E20]", text: "text-[#1B5E20]", chip: "bg-emerald-50", v: leadStats?.converted || 0 },
    { k: "dropped", label: "Dropped", fill: "bg-red-400", text: "text-red-600", chip: "bg-red-50", v: leadStats?.dropped || 0 },
  ];
  const ticketTotal = ticketStats?.total || 1;
  const ticketSegs = [
    { k: "open", label: "Open", fill: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-50", v: ticketStats?.open || 0 },
    { k: "closed", label: "Resolved", fill: "bg-[#1B5E20]", text: "text-[#1B5E20]", chip: "bg-emerald-50", v: ticketStats?.closed || 0 },
    { k: "overdue", label: "Overdue", fill: "bg-rose-500", text: "text-rose-700", chip: "bg-rose-50", v: ticketStats?.overdue || 0 },
  ];
  return (
    <div className="space-y-3" data-testid="sc-status-infographic">
      <Link to="/sc/leads" className="block rounded-2xl p-4 bg-white border-2 border-sky-100 shadow-[0_4px_14px_rgba(2,132,199,0.08)] hover:border-sky-300 transition-all" data-testid="sc-infographic-leads">
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-sky-600" /> New Requests
          </div>
          <div className="flex items-center gap-1 text-sky-700">
            <span className="font-mono text-lg font-black">{Object.values(leadStats || {}).reduce((a, b) => a + b, 0)}</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 gap-[1.5px]">
          {leadSegs.map((s) => s.v > 0 && (
            <div key={s.k} className={s.fill} style={{ width: `${(s.v / leadTotal) * 100}%` }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {leadSegs.map((s) => (
            <span key={s.k} className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${s.chip} ${s.text}`}>
              {s.label} · {s.v}
            </span>
          ))}
        </div>
      </Link>
      <Link to="/sc/service-requests" className="block rounded-2xl p-4 bg-white border-2 border-amber-100 shadow-[0_4px_14px_rgba(217,119,6,0.08)] hover:border-amber-300 transition-all" data-testid="sc-infographic-tickets">
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <Wrench className="w-3.5 h-3.5 text-amber-600" /> Service Requests
          </div>
          <div className="flex items-center gap-1 text-amber-700">
            <span className="font-mono text-lg font-black">{ticketStats?.total || 0}</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 gap-[1.5px]">
          {ticketSegs.map((s) => s.v > 0 && (
            <div key={s.k} className={s.fill} style={{ width: `${(s.v / ticketTotal) * 100}%` }} />
          ))}
        </div>
        {ticketStats?.total ? (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {ticketSegs.map((s) => (
              <span key={s.k} className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${s.chip} ${s.text}`}>
                {s.label} · {s.v}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 mt-2.5 flex items-center gap-1 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> No service requests yet</div>
        )}
      </Link>
    </div>
  );
}

const FUNNEL_STEPS = [
  { key: "submitted", label: "Submitted" },
  { key: "accepted", label: "Accepted" },
  { key: "site_survey", label: "Site Survey" },
  { key: "quotation", label: "Quotation" },
  { key: "installation", label: "Installed" },
];

const CARD = "bg-white border border-slate-200 rounded-2xl shadow-[0_2px_8px_rgba(15,23,42,0.04)]";

function StatTile({ label, value, sub, accent = "text-slate-900", to, testid }) {
  const content = (
    <div className={`${CARD} p-3.5` + (to ? " hover:border-emerald-300 hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)] transition-all duration-150" : "")} data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

export default function ScHome() {
  const { user } = useAuth();
  const { data } = useRealtime("/sc/dashboard", { interval: 120000 });

  if (!data) return <div className="font-mono text-xs text-slate-400 mt-10 text-center">Loading…</div>;

  const earnings = data.earnings || {};
  const counts = earnings._counts || {};

  return (
    <div className="space-y-6 pb-8" data-testid="sc-home">
      {/* Hero strip */}
      <div className="bg-gradient-to-br from-[#1B5E20] to-[#134A18] p-5 rounded-2xl shadow-[0_4px_20px_rgba(27,94,32,0.25)] relative overflow-hidden">
        <div className="flex items-start justify-between gap-3 relative">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-200">Lifetime earnings</div>
            <div className="font-brand text-4xl font-semibold tracking-tight text-white mt-1" data-testid="sc-paid-total">
              ₹{Number(earnings.paid || 0).toLocaleString("en-IN")}
            </div>
            <div className="text-[11px] text-emerald-100/80 mt-2">
              {counts.paid || 0} paid · {counts.processing || 0} processing · {counts.pending || 0} pending
            </div>
          </div>
          {data.rank && (
            <div className="text-center bg-white text-[#B45309] px-3 py-2 rounded-xl shadow-md flex-shrink-0">
              <div className="font-mono text-[9px] uppercase tracking-widest font-bold text-slate-500">Rank</div>
              <div className="font-mono text-2xl font-bold leading-none mt-0.5">#{data.rank}</div>
            </div>
          )}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatTile label="Total leads" value={data.total_leads} sub={`${data.lead_stats?.accepted || 0} accepted`} accent="text-sky-700" to="/sc/leads" testid="sc-stat-leads" />
        <StatTile
          label="Win streak"
          value={<span className="flex items-center gap-1"><Flame className="w-5 h-5 text-[#D97706]" />{data.streak}</span>}
          sub="consecutive paid"
        />
        <StatTile
          label="Won opps"
          value={(data.opp_stats?.won?.count || 0) + (data.opp_stats?.won_verbal?.count || 0)}
          sub="closed deals"
          accent="text-[#1B5E20]"
          to="/sc/leads"
          testid="sc-stat-won"
        />
        <StatTile
          label="Pending payout"
          value={`₹${Number(earnings.pending || 0).toLocaleString("en-IN")}`}
          sub={`${counts.pending || 0} opps`}
          accent="text-[#B45309]"
          to="/sc/earnings"
          testid="sc-stat-pending-payout"
        />
      </div>

      <StatusInfographic leadStats={data.lead_stats} ticketStats={data.ticket_stats} />

      <TodayStrip todayLeads={data.today_leads || 0} todayTickets={data.today_tickets || 0} />

      <ScLeadBanner />

      {/* Funnel */}
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">Referral funnel</div>
        <div className={`${CARD} p-3 grid grid-cols-5 gap-1.5`} data-testid="sc-funnel">
          {FUNNEL_STEPS.map((s, i) => {
            const val = data.funnel?.[s.key] ?? 0;
            const prevVal = i === 0 ? val : (data.funnel?.[FUNNEL_STEPS[i - 1].key] ?? 0);
            const pct = prevVal > 0 ? Math.round((val / prevVal) * 100) : (i === 0 ? 100 : 0);
            return (
              <div key={s.key} className="text-center" data-testid={`sc-funnel-${s.key}`}>
                <div className="font-mono text-lg font-bold text-[#1B5E20]">{val}</div>
                <div className="text-[8px] font-semibold uppercase tracking-wider text-slate-500 leading-tight mt-0.5">{s.label}</div>
                {i > 0 && <div className="text-[8px] text-slate-400 mt-0.5">{pct}%</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversion health */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`${CARD} p-3.5`} data-testid="sc-conversion-rate">
          <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Lead → Win rate</div>
          <div className="font-mono text-2xl font-bold tracking-tight mt-1 text-[#1B5E20]">{data.conversion?.lead_to_win_pct ?? 0}%</div>
        </div>
        <div className={`${CARD} p-3.5`} data-testid="sc-kw-rank">
          <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center justify-between">
            kW Rank
            {data.conversion?.movement === "up" && <TrendingUp className="w-3 h-3 text-emerald-600" />}
            {data.conversion?.movement === "down" && <TrendingDown className="w-3 h-3 text-red-500" />}
            {data.conversion?.movement === "same" && <Minus className="w-3 h-3 text-slate-400" />}
          </div>
          <div className="font-mono text-2xl font-bold tracking-tight mt-1 text-slate-900">
            {data.conversion?.kw_rank ? `#${data.conversion.kw_rank}` : "—"}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">{data.conversion?.kw_total ?? 0} kW installed</div>
        </div>
      </div>

      {/* Action cards */}
      {(data.action_cards?.cold_leads?.length > 0 || data.action_cards?.quotations_pending?.length > 0 || data.action_cards?.on_hold?.length > 0) && (
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#B45309] mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> Needs your attention
          </div>
          <div className="space-y-1.5" data-testid="sc-action-cards">
            {data.action_cards.quotations_pending?.map((o) => (
              <Link key={`q-${o.id}`} to={`/sc/leads?open=${o.id}&kind=opportunity`} className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-2.5 rounded-xl text-[11px] hover:border-amber-300 active:scale-[0.99] transition-all" data-testid={`sc-action-quote-${o.id}`}>
                <FileWarning className="w-3.5 h-3.5 text-[#B45309] flex-shrink-0" />
                <span className="flex-1 truncate text-amber-900">{o.code} · {o.name} — quote awaiting customer decision</span>
                <ChevronRight className="w-3 h-3 text-amber-500" />
              </Link>
            ))}
            {data.action_cards.cold_leads?.map((l) => (
              <Link key={`c-${l.id}`} to={`/sc/leads?open=${l.id}&kind=lead`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2.5 rounded-xl text-[11px] hover:border-slate-300 active:scale-[0.99] transition-all" data-testid={`sc-action-cold-${l.id}`}>
                <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <span className="flex-1 truncate text-slate-700">{l.code} · {l.name} — no update in {l.days}d</span>
                <ChevronRight className="w-3 h-3 text-slate-400" />
              </Link>
            ))}
            {data.action_cards.on_hold?.map((o) => (
              <Link key={`h-${o.kind}-${o.id}`} to={`/sc/leads?open=${o.id}&kind=${o.kind}`} className="flex items-center gap-2 bg-orange-50 border border-orange-200 px-3 py-2.5 rounded-xl text-[11px] hover:border-orange-300 active:scale-[0.99] transition-all" data-testid={`sc-action-hold-${o.id}`}>
                <PauseCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                <span className="flex-1 truncate text-orange-900">{o.code} · {o.name} — on hold{o.reason ? `: ${o.reason}` : ""}</span>
                <ChevronRight className="w-3 h-3 text-orange-400" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Badges */}
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-1.5">
          <Award className="w-3 h-3" /> Achievements
        </div>
        <div className="grid grid-cols-4 gap-2" data-testid="sc-badges">
          {data.badges.map((b) => {
            const Icon = ICON_MAP[b.icon] || BadgeCheck;
            return (
              <div
                key={b.id}
                data-testid={`badge-${b.id}`}
                className={"border p-2.5 text-center rounded-xl transition-all " +
                  (b.earned
                    ? "border-emerald-200 bg-emerald-50 shadow-sm"
                    : "border-slate-200 bg-slate-50 opacity-60")}
              >
                <Icon className={"w-5 h-5 mx-auto " + (b.earned ? "text-[#1B5E20]" : "text-slate-400")} strokeWidth={2} />
                <div className="text-[8px] font-semibold uppercase tracking-wider mt-1.5 leading-tight text-slate-600">{b.label}</div>
                {!b.earned && b.target && (
                  <div className="text-[8px] text-slate-400 mt-0.5">{b.progress}/{b.target}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* City leaderboard */}
      {data.leaderboard_city?.top5?.length > 0 && (
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-1.5">
            <MapPin className="w-3 h-3" /> Top SCs · {data.leaderboard_city.city} (kW installed)
          </div>
          <div className={`${CARD} divide-y divide-slate-100`} data-testid="sc-city-leaderboard">
            {data.leaderboard_city.top5.map((r) => (
              <div key={r.rank} className={"flex items-center gap-3 px-3 py-2.5 " + (r.is_me ? "bg-emerald-50" : "")}>
                <div className={"font-mono font-bold w-7 text-center " + (r.rank === 1 ? "text-[#D97706] text-lg" : "text-slate-400 text-sm")}>
                  #{r.rank}
                </div>
                <div className="flex-1 text-sm truncate text-slate-900">
                  {r.name}
                  {r.is_me && <span className="text-[9px] font-semibold uppercase tracking-wider ml-1 text-[#1B5E20]">You</span>}
                </div>
                <div className="font-mono text-xs text-[#1B5E20] font-semibold flex items-center gap-1"><Users className="w-3 h-3" />{r.kw} kW</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {data.leaderboard_top?.length > 0 && (
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-1.5">
            <Trophy className="w-3 h-3" /> Top SCs · Lifetime
          </div>
          <div className={`${CARD} divide-y divide-slate-100`} data-testid="sc-leaderboard">
            {data.leaderboard_top.map((r) => (
              <div key={r.rank} className={"flex items-center gap-3 px-3 py-2.5 " + (r.name === user?.name ? "bg-emerald-50" : "")}>
                <div className={"font-mono font-bold w-7 text-center " +
                  (r.rank === 1 ? "text-[#D97706] text-lg" : "text-slate-400 text-sm")}>
                  #{r.rank}
                </div>
                <div className="flex-1 text-sm truncate text-slate-900">
                  {r.name}
                  {r.name === user?.name && (
                    <span className="text-[9px] font-semibold uppercase tracking-wider ml-1 text-[#1B5E20]">You</span>
                  )}
                </div>
                <div className="font-mono text-xs text-[#1B5E20] font-semibold">₹{Number(r.paid_total).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/sc/leads"
          className={`${CARD} p-3.5 hover:border-emerald-300 flex items-center justify-between active:scale-[0.98] transition-all`}
          data-testid="quick-leads"
        >
          <div className="text-sm font-semibold text-slate-900">My Leads</div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
        <Link
          to="/sc/service-requests"
          className={`${CARD} p-3.5 hover:border-emerald-300 flex items-center justify-between active:scale-[0.98] transition-all`}
          data-testid="quick-service-requests"
        >
          <div className="text-sm font-semibold flex items-center gap-1.5 text-slate-900">
            <Wrench className="w-3.5 h-3.5 text-[#1B5E20]" /> Service Requests
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
        <Link
          to="/sc/marketing"
          className={`${CARD} p-3.5 hover:border-emerald-300 flex items-center justify-between active:scale-[0.98] transition-all col-span-2`}
          data-testid="quick-schemes"
        >
          <div className="text-sm font-semibold flex items-center gap-1.5 text-slate-900">
            <Sparkles className="w-3.5 h-3.5 text-[#D97706]" /> Toolkit
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
      </div>
    </div>
  );
}
